import { useCompanyApi } from '@/api/client/api-client';
import { notifyError } from '@/bugsnag';
import type { Client, TPSOrderInfo } from '@/company-portal/add-file/add-file.types';
import type {
    AddFileConfirmationSummary,
    AvailableWireDoc,
    File,
    UserWithBranchesGroup,
} from '@/components/Views/Company/File/file.types';
import { ClientTypeEnum } from '@/components/Views/Company/File/file.types';
import { useFileDocumentStore } from '@/stores/file-document.store';
import type { Branch, Company } from '@/types/closinglock.types';
import { defineStore } from 'pinia';
import type { DateValue } from 'reka-ui';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue } from 'vue';

export const useAddFileStore = defineStore('addFile', () => {
    const companyApi = useCompanyApi({ auth: 'jwt' });

    const fileDocumentStore = useFileDocumentStore();

    // file details
    const refNumber = ref('');
    const address = ref('');
    const company = ref<Company>();
    const branch = ref<Branch>();
    const closeDate = ref<DateValue>();
    const balanceDue = ref<number>();
    const notes = ref('');

    // send wire instructions
    const wireDoc = ref<AvailableWireDoc>();
    const notificationUsers = ref<UserWithBranchesGroup[]>([]);
    const notifyClients = ref(true);
    const wireDocRecipientClientIds = ref<Client['id'][]>([]);

    // request wire instructions
    const requestWireInfoClientIds = ref<Client['id'][]>([]);

    // request IDV
    const requestIdvClientIds = ref<Client['id'][]>([]);

    // TPS prepopulated order
    const tpsOrder = ref<TPSOrderInfo>();

    // @todo [CLENG-1157] static placeholder summary for the "Create this file?" confirmation modal.
    // Replace with real form-derived data once the modal is wired into submission.
    const confirmationSummary = ref<AddFileConfirmationSummary>({
        fileDetails: {
            referenceNumber: 'AE424',
            propertyAddress: '1234 Maple Street, South Ridge, CA',
            company: 'National Title Co',
            branch: 'Austin Branch',
            closingDate: '03/24/2026',
            balanceDue: '---',
        },
        clients: [
            {
                id: 1,
                first: 'Jimmy',
                last: 'James',
                role: ClientTypeEnum.BUYER,
                email: 'Jimmy.James@gmail.com',
                phone: '(435) 465-2849',
                tasks: {
                    requestWireInstructions: true,
                    requestIdentityVerification: true,
                },
                wireInstructionsDocumentName: 'Wire Instructions Document.pdf',
                documents: [{ name: 'ESign Document.pdf', needsEsign: true }],
            },
            {
                id: 2,
                first: 'Linda',
                last: 'Morris',
                role: ClientTypeEnum.SELLER,
                email: 'Linda.Morris@gmail.com',
                phone: '(512) 555-0121',
                tasks: {
                    requestWireInstructions: true,
                    requestIdentityVerification: false,
                },
                wireInstructionsDocumentName: null,
                documents: [
                    { name: 'Seller Authorization Form.pdf', needsEsign: true },
                    { name: 'Seller Contract.pdf', needsEsign: false },
                    { name: 'Non disclosure agreement.pdf', needsEsign: true },
                ],
            },
        ],
    });

    const parseTpsOrder = (tpsOrderString: string | undefined): TPSOrderInfo | undefined => {
        if (!tpsOrderString) {
            return;
        }
        try {
            return JSON.parse(tpsOrderString);
        } catch (error) {
            notifyError(error, 'useAddFileStore:parseTpsOrder', {
                tpsOrderJsonString: tpsOrderString,
            });
        }
    };

    const ingestTpsOrder = (tpsOrderString: string) => {
        tpsOrder.value = parseTpsOrder(tpsOrderString);
    };

    const resetAddFileStore = () => {
        refNumber.value = '';
        address.value = '';
        notificationUsers.value = [];
        notifyClients.value = true;
        closeDate.value = undefined;
        balanceDue.value = undefined;
        notes.value = '';
        wireDocRecipientClientIds.value = [];
        requestWireInfoClientIds.value = [];
        requestIdvClientIds.value = [];
        fileDocumentStore.clearAllDocuments();
    };

    const checkRefNumberIsUnique = (refNumberToCheck: Ref<string>, excludeFileId?: MaybeRefOrGetter<File['id']>) => {
        const url = computed(() => {
            if (!refNumberToCheck.value) return '';
            const fileId = toValue(excludeFileId);
            const params = new URLSearchParams({
                ref_number: refNumberToCheck.value,
                exclude_file_id: fileId?.toString() || '',
            });
            return `/api/user/files/validate/ref-number-in-use?${params.toString()}`;
        });

        const { data, isFetching } = companyApi(url, {
            refetch: true,
            immediate: false,
            beforeFetch(ctx) {
                if (!refNumberToCheck.value) ctx.cancel();
            },
            onFetchError(ctx) {
                notifyError(ctx.error, 'useAddFileStore::checkRefNumberIsUnique', {
                    ctx,
                    url: url.value,
                });
                return ctx;
            },
        }).json<{ data: { matchingFileId: number | null } }>();

        return {
            checkResult: data,
            isChecking: isFetching,
        };
    };

    return {
        // file details
        refNumber,
        address,
        company,
        branch,
        closeDate,
        balanceDue,
        notes,

        // send wire instructions
        wireDoc,
        wireDocRecipientClientIds,

        // notification
        notificationUsers,
        notifyClients,

        // request wire instructions
        requestWireInfoClientIds,

        // request IDV
        requestIdvClientIds,

        // tps
        tpsOrder,
        ingestTpsOrder,

        // form submission
        checkRefNumberIsUnique,
        confirmationSummary,
        resetAddFileStore,
    };
});
